import React, { useContext, useRef, useEffect, useState } from 'react';
import routes from 'config/MenuNavConstants';
import { validatePermission } from 'actions';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useWindowSize from 'hooks/useWindowSize';
import UpAnchor from 'v3/components/UpAnchor';
import * as Styled from '../styles';
import { FaBell, FaChevronDown, FaChevronRight, FaSearch } from 'react-icons/fa';
import { useSidebar } from './SidebarContext';
import { UserContext } from 'contexts/UserContext';
import OneSignal from 'react-onesignal';

function normalizePath(value) {
  if (!value || typeof value !== 'string') return '';
  return value.replace(/\/+$/, '') || '/';
}

function isRouteActive(currentPath, routePath) {
  const current = normalizePath(currentPath);
  const route = normalizePath(routePath);

  if (!current || !route) return false;
  if (current === route) return true;
  if (route === '/') return current === '/';
  if (current.startsWith(`${route}/`)) return true;

  // Supports route patterns with dynamic params (e.g. /clientes/:id)
  if (route.includes(':')) {
    const routePattern = route.replace(/:[^/]+/g, '[^/]+');
    const regex = new RegExp(`^${routePattern}$`);
    return regex.test(current);
  }

  return false;
}

function getMostSpecificActivePath(currentPath, routes = []) {
  const matches = routes
    .map((route) => route?.path)
    .filter(Boolean)
    .filter((routePath) => isRouteActive(currentPath, routePath))
    .sort((a, b) => normalizePath(b).length - normalizePath(a).length);

  return matches[0] || null;
}

function sortMenuChildren(a, b) {
  const aOrder = Number.isFinite(a?.order) ? a.order : null;
  const bOrder = Number.isFinite(b?.order) ? b.order : null;

  if (aOrder !== null && bOrder !== null) return aOrder - bOrder;
  if (aOrder !== null) return -1;
  if (bOrder !== null) return 1;

  return a.title.localeCompare(b.title);
}

const Items = ({ link, path, isMenuOpen, onLinkClick }) => {
  const hasChildren = !!link.children?.length;
  const visibleChildren = (link.children || [])
    .filter(({ hidden }) => !hidden)
    .filter(({ permissionRequired }) =>
      permissionRequired ? validatePermission(permissionRequired) : true
    );

  const activeChildPath = getMostSpecificActivePath(path, visibleChildren);
  const hasActiveChild = !!activeChildPath;
  const isActive = isRouteActive(path, link.path) || hasActiveChild;
  const activeBackground = 'rgba(255, 255, 255, 0.16)';
  const [isOpen, setIsOpen] = React.useState(hasActiveChild);

  useEffect(() => {
    if (hasActiveChild) {
      setIsOpen(true);
    }
  }, [hasActiveChild]);

  const handleClick = () => {
    if (hasChildren) {
      setIsOpen(prev => !prev);
    }
  };

  return (
    <div style={{ width: '100%' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '8px',
          cursor: hasChildren ? 'pointer' : 'default',
          color: 'white',
          borderRadius: 4,
          transition: 'background-color 0.2s',
          backgroundColor: isActive ? activeBackground : 'transparent',
          borderLeft: isActive ? '3px solid rgba(255, 255, 255, 0.65)' : '3px solid transparent',
          fontSize: 14,
        }}
        onClick={handleClick}
        onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#2b5b84')}
        onMouseLeave={e =>
          (e.currentTarget.style.backgroundColor =
            isActive ? activeBackground : 'transparent')
        }
      >
        <div style={{ width: 25 }}>{link.icon}</div>
        {isMenuOpen &&
          (hasChildren ? (
            <>
              <span style={{ flex: 1 }}>{link.title}</span>
              {isOpen ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
            </>
          ) : (
            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                height: '100%',
              }}
              onClick={e => e.stopPropagation()}
            >
                <Link
                  to={link.path}
                  onClick={() => onLinkClick?.()}
                  style={{
                    color: 'white',
                    textDecoration: 'none',
                    width: '100%',
                    fontSize: 14,
                  }}
                >
                {link.title}
              </Link>
            </div>
          ))}
      </div>
      {hasChildren && isOpen && isMenuOpen && (
        <div
          style={{
            paddingLeft: 36,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#1f4c70',
            padding: 8,
            borderRadius: 4,
          }}
        >
          {[...visibleChildren]
            .sort(sortMenuChildren)
            .map((child, index) => {
              const isChildActive = activeChildPath === child.path;

              return (
                <div key={child.path || `${link.title}-${child.title}-${index}`}>
                <Link
                  to={child.path}
                  onClick={() => onLinkClick?.()}
                  style={{
                    color: isChildActive ? '#ffffff' : '#d8e6f1',
                    fontSize: 13.5,
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '6px 8px',
                    borderRadius: 4,
                    transition: 'background-color 0.2s',
                    background: isChildActive ? 'rgba(255, 255, 255, 0.14)' : 'transparent',
                    borderLeft: isChildActive
                      ? '3px solid rgba(255, 255, 255, 0.65)'
                      : '3px solid transparent',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#336d9c')}
                  onMouseLeave={e =>
                    (e.currentTarget.style.backgroundColor = isChildActive
                      ? 'rgba(255, 255, 255, 0.14)'
                      : 'transparent')
                  }
                >
                  <span style={{ fontSize: 10, marginTop: 2 }}>•</span>
                  {child.title}
                </Link>
                {index < visibleChildren.length - 1 && (
                  <hr
                    style={{
                      borderColor: '#2f6e9c',
                      width: '90%',
                      margin: '4px auto',
                    }}
                  />
                )}
              </div>
              );
            })}
        </div>
      )}
    </div>
  );
};

function wait(milliseconds) {
  return new Promise(resolve => setTimeout(resolve, milliseconds));
}

function withTimeout(promise, milliseconds, message) {
  let timeoutId;

  const timeout = new Promise((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(message)), milliseconds);
  });

  return Promise.race([promise, timeout]).finally(() => clearTimeout(timeoutId));
}

const NOTIFICATION_BELL_VISUAL_STATE_KEY = 'oneSignalBellVisualState';

export default function Sidebar() {
  const path = useSelector(store => store.path);
  const [windowSize] = useWindowSize();
  const { isMenuOpen, openMenu, closeMenu } = useSidebar();
  // Redesenha o menu quando as permissões do usuário são recarregadas.
  useContext(UserContext);
  const menuRef = useRef();
  const notificationRef = useRef();
  const [isNotificationPopoverOpen, setIsNotificationPopoverOpen] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState(
    window.Notification?.permission
  );
  const [isPushOptedIn, setIsPushOptedIn] = useState(
    localStorage.getItem(NOTIFICATION_BELL_VISUAL_STATE_KEY) === 'enabled' ||
      OneSignal.User?.PushSubscription?.optedIn === true
  );
  const [showNotificationReloadBanner, setShowNotificationReloadBanner] = useState(false);
  const [notificationActionMessage, setNotificationActionMessage] = useState('');
  const [search, setSearch] = React.useState('');
  const [hoverOpened, setHoverOpened] = useState(false);
  const currentHtmlFullHeight = document.querySelector('html');
  const { height } = currentHtmlFullHeight.getBoundingClientRect();

  useEffect(() => {
    function handleClickOutside(event) {
      if (isMenuOpen && menuRef.current && !menuRef.current.contains(event.target)) {
        closeMenu();
        setHoverOpened(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen, closeMenu]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        isNotificationPopoverOpen &&
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setIsNotificationPopoverOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isNotificationPopoverOpen]);

  const isNotificationBlocked = notificationPermission === 'denied';
  const isNotificationEnabled = isPushOptedIn;

  function showNotificationMessage(message) {
    setNotificationActionMessage(message);
    window.setTimeout(() => {
      setNotificationActionMessage('');
    }, 5000);
  }

  async function enableNotifications(event) {
    event?.preventDefault();
    event?.stopPropagation();

    try {
      if (window.Notification?.permission === 'denied') {
        setNotificationPermission('denied');
        return;
      }

      let permissionWasGranted = window.Notification?.permission === 'granted';

      if (!permissionWasGranted && window.Notification?.requestPermission) {
        const permission = await window.Notification.requestPermission();
        permissionWasGranted = permission === 'granted';
      }

      if (
        window.Notification?.permission !== 'granted' &&
        !OneSignal.Notifications?.permission &&
        OneSignal.Notifications?.requestPermission
      ) {
        let oneSignalPermissionWasGranted = false;

        try {
          oneSignalPermissionWasGranted = await withTimeout(
            OneSignal.Notifications.requestPermission(),
            5000,
            'OneSignal requestPermission timed out after 5000ms'
          );
        } catch (error) {
          // Keep the visual flow aligned with OneSignal even if the SDK stalls locally.
        }

        permissionWasGranted = permissionWasGranted || oneSignalPermissionWasGranted;
      }

      setNotificationPermission(window.Notification?.permission);

      if (permissionWasGranted) {
        setIsNotificationPopoverOpen(false);
        setShowNotificationReloadBanner(true);
        setIsPushOptedIn(true);
        showNotificationMessage('Obrigado por aceitar receber notificações!');
        localStorage.setItem(NOTIFICATION_BELL_VISUAL_STATE_KEY, 'enabled');
        localStorage.setItem('isPushNotificationsEnabled', 'true');

        if (OneSignal.User?.PushSubscription?.optIn) {
          await withTimeout(
            OneSignal.User.PushSubscription.optIn(),
            8000,
            'OneSignal optIn timed out after 8000ms'
          );
          await wait(500);
          if (OneSignal.User.PushSubscription.id) {
            localStorage.setItem('oneSignalPlayerId', OneSignal.User.PushSubscription.id);
          }
        }
        setIsPushOptedIn(true);
        setNotificationPermission('granted');
      }
    } catch (error) {
      // Notification permission can fail depending on browser/site settings.
    }
  }

  async function disableNotifications(event) {
    event?.preventDefault();
    event?.stopPropagation();

    try {
      localStorage.setItem('isPushNotificationsEnabled', 'false');
      localStorage.setItem(NOTIFICATION_BELL_VISUAL_STATE_KEY, 'disabled');
      setIsPushOptedIn(false);
      setNotificationPermission(window.Notification?.permission);
      setIsNotificationPopoverOpen(false);
      setShowNotificationReloadBanner(true);
      showNotificationMessage('Você não receberá mais notificações');

      if (OneSignal.User?.PushSubscription?.optOut) {
        await OneSignal.User.PushSubscription.optOut();
      }
    } catch (error) {
      // Notification opt-out can fail depending on browser/site settings.
    }
  }

  return (
    <Styled.DefaultContainer>
      {showNotificationReloadBanner && (
        <Styled.NotificationReloadBanner>
          <span>⚙</span>
          <p>Recarregue esta página para aplicar as configurações atualizadas ao site.</p>
          <button type="button" onClick={() => window.location.reload()}>
            Recarregar
          </button>
        </Styled.NotificationReloadBanner>
      )}

      <UpAnchor reachedEnd={windowSize.userScroll.y >= 300} isMenuOpen={isMenuOpen} />
      <Styled.BackDrop
        $heigth={
          height > windowSize.rootComponent.height
            ? `${height}px`
            : `${windowSize.rootComponent.height}px`
        }
      />
      <Styled.MenuContainer
        $isOpen={isMenuOpen}
        ref={menuRef}
        style={{ cursor: 'default' }}
        onMouseEnter={() => {
          if (!isMenuOpen) {
            openMenu();
            setHoverOpened(true);
          }
        }}
        onMouseLeave={() => {
          if (hoverOpened) {
            closeMenu();
            setHoverOpened(false);
          }
        }}
      >
        {isMenuOpen && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 10px',
              borderRadius: 6,
              background: 'rgba(255, 255, 255, 0.08)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              marginBottom: 12,
            }}
          >
            <FaSearch size={14} color="rgba(255, 255, 255, 0.7)" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Pesquise a página aqui"
              style={{
                background: 'transparent',
                border: 0,
                outline: 0,
                color: '#fff',
                width: '100%',
                fontSize: 13.5,
              }}
            />
          </div>
        )}
        <Styled.ScrollArea $isOpen={isMenuOpen}>
          {isMenuOpen && search.trim().length > 0 ? (
            routes
              .filter(({ hidden }) => !hidden)
              .filter(({ permissionRequired }) => validatePermission(permissionRequired))
              .flatMap(link => {
                const items = [];
                if (link.path) {
                  items.push({ title: link.title, path: link.path });
                }
                if (link.children?.length) {
                  link.children.forEach(child => {
                    if (child.hidden) return;
                    if (
                      child.permissionRequired &&
                      !validatePermission(child.permissionRequired)
                    ) {
                      return;
                    }
                    items.push({
                      title: `${link.title} > ${child.title}`,
                      path: child.path,
                    });
                  });
                }
                return items;
              })
              .filter(item =>
                item.title.toLowerCase().includes(search.trim().toLowerCase())
              )
              .map(item => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => {
                    setSearch('');
                    closeMenu();
                    setHoverOpened(false);
                  }}
                  style={{
                    color: '#fff',
                    textDecoration: 'none',
                    fontSize: 13.5,
                    padding: '6px 8px',
                    borderRadius: 6,
                  }}
                >
                  {item.title}
                </Link>
              ))
          ) : (
            routes
              .filter(({ hidden }) => !hidden)
              .filter(({ permissionRequired }) =>
                permissionRequired ? validatePermission(permissionRequired) : true
              )
              .map((link, index) => (
                <Items
                  key={link.path || `${link.title}-${index}`}
                  link={link}
                  path={path}
                  isMenuOpen={isMenuOpen}
                  onLinkClick={closeMenu}
                />
              ))
          )}
        </Styled.ScrollArea>
      </Styled.MenuContainer>

      <Styled.OneSignalNotificationControl ref={notificationRef} $isOpen={isMenuOpen}>
        <button
          type="button"
          className="notification-bell"
          onClick={() => setIsNotificationPopoverOpen(prev => !prev)}
        >
          <FaBell />
        </button>

        {(!isNotificationPopoverOpen || notificationActionMessage) && (
          <span
            className={`notification-tooltip ${
              notificationActionMessage ? 'is-visible' : ''
            }`}
          >
            {notificationActionMessage || (isNotificationBlocked
              ? 'Você bloqueou as notificações'
              : isNotificationEnabled
                ? 'Você está recebendo notificações'
                : 'Clique para receber notificações')}
          </span>
        )}

        {isNotificationPopoverOpen && (
          <div className="notification-popover">
            {isNotificationBlocked ? (
              <>
                <h3>Desbloqueie as Notificações</h3>
                <p>Siga essas instruções para assinar as notificações:</p>
                <a
                  className="permission-preview"
                  href="https://media.onesignal.com/web-sdk/bell/chrome-unblock.jpg"
                  target="_blank"
                  rel="noreferrer"
                >
                  <img
                    src="https://media.onesignal.com/web-sdk/bell/chrome-unblock.jpg"
                    alt="Passo a passo para desbloquear notificações no Chrome"
                  />
                </a>
              </>
            ) : (
              <>
                <h3>Gerenciar Notificações</h3>
                <div className="notification-preview" aria-hidden="true">
                  <span />
                  <div>
                    <i />
                    <i />
                    <i />
                    <i />
                  </div>
                </div>
                <button
                  type="button"
                  className="notification-action"
                  onClick={isNotificationEnabled ? disableNotifications : enableNotifications}
                >
                  {isNotificationEnabled ? 'Recusar' : 'Aceitar'}
                </button>
              </>
            )}
          </div>
        )}
      </Styled.OneSignalNotificationControl>
    </Styled.DefaultContainer>
  );
}
